import { addDays, format, startOfDay } from 'date-fns';
import type { InventoryItem, InventoryTransaction, PartCategory } from '@/types';
import type { StockMovementItem } from '@/hooks/useInventory';
import type { CalendarHours } from '@/hooks/useTenantSettings';

export function getStockStatus(item: InventoryItem): 'in_stock' | 'low_stock' | 'out_of_stock' {
  if (item.quantity === 0) return 'out_of_stock';
  if (item.quantity <= item.minStockLevel) return 'low_stock';
  return 'in_stock';
}

// The detail modal renders four transaction types; the backend records more
// granular movement types. Collapse each to the closest display bucket.
function movementTypeToTransactionType(movementType: string): InventoryTransaction['type'] {
  switch (movementType) {
    case 'purchase':
      return 'purchase';
    case 'sale':
    case 'sales':
    case 'work_order_consumption':
      return 'sale';
    case 'return':
      return 'return';
    // adjustment, count, damage, transfer and anything else → adjustment
    default:
      return 'adjustment';
  }
}

/**
 * Map a backend stock movement to the modal's InventoryTransaction shape.
 * The signed quantity comes from the actual on-hand delta (quantityAfter −
 * quantityBefore), which stays correct regardless of how the raw `quantity`
 * field is signed for a given movement type. #1166
 */
export function mapMovementToTransaction(
  partId: string,
  movement: StockMovementItem,
): InventoryTransaction {
  return {
    id: movement.id,
    warehouseName: movement.warehouseName ?? undefined,
    binLocationCode: movement.binLocationCode ?? undefined,
    itemId: partId,
    type: movementTypeToTransactionType(movement.movementType),
    quantity: movement.quantityAfter - movement.quantityBefore,
    unitPriceCents: movement.unitCostCents,
    notes: movement.reason ?? undefined,
    createdAt: movement.createdAt,
    createdBy: movement.performedByName,
  };
}

export const partCategoryLabels: Record<PartCategory, string> = {
  oil: 'Öljyt',
  filters: 'Suodattimet',
  brakes: 'Jarrut',
  tires: 'Renkaat',
  electrical: 'Sähkö',
  suspension: 'Alusta',
  engine: 'Moottori',
  body: 'Kori',
  other: 'Muut',
};

/** Parse "HH:MM" to fractional hours (e.g. "17:30" → 17.5) */
export function parseTimeToHours(time: string): number {
  const parts = time.split(':').map(Number);
  return (parts[0] ?? 0) + (parts[1] ?? 0) / 60;
}

/** Returns business hours ±2h range for day view scaling */
export function getBusinessHoursRange(hours: CalendarHours): { startHour: number; endHour: number } {
  let earliest = 24;
  let latest = 0;
  for (const daySlots of Object.values(hours)) {
    for (const slot of daySlots) {
      earliest = Math.min(earliest, Math.floor(parseTimeToHours(slot.start)));
      latest = Math.max(latest, Math.ceil(parseTimeToHours(slot.end)));
    }
  }
  return {
    startHour: Math.max(0, earliest - 2),
    endHour: Math.min(24, latest + 2),
  };
}

/** Returns the actual earliest business start time as "HH:MM" (no padding) */
export function getEarliestBusinessStart(hours: CalendarHours): string {
  let earliestTime = '23:59';
  for (const daySlots of Object.values(hours)) {
    for (const slot of daySlots) {
      if (slot.start < earliestTime) {
        earliestTime = slot.start;
      }
    }
  }
  return earliestTime;
}

/** Checks if there are any weekend business hours (Saturday=6, Sunday=7) */
export function hasWeekendHours(hours: CalendarHours): boolean {
  return (hours[6]?.length ?? 0) > 0 || (hours[7]?.length ?? 0) > 0;
}

/** A span of a single day, in minutes from local midnight (end exclusive) */
export interface MinuteRange {
  startMinutes: number;
  endMinutes: number;
}

/**
 * Per-date overrides of the weekly hours (#1282), indexed by local 'yyyy-MM-dd'.
 * An override REPLACES the weekday's hours for that date — a holiday closes a
 * normally-open day, and an exceptional opening opens a normally-closed one.
 */
export type HoursExceptionIndex = Map<
  string,
  { isClosed: boolean; open?: string | undefined; close?: string | undefined }
>;

/**
 * Indexes exceptions for date lookup. The key is the LOCAL date — the calendar
 * works in local time throughout, and `toISOString().slice(0, 10)` would land on
 * the previous day for any positive UTC offset (Finland is +2/+3).
 */
export function buildHoursExceptionIndex(
  exceptions: {
    date: string;
    isClosed: boolean;
    open?: string;
    close?: string;
    /** Accepted so a `HoursException` can be passed as-is; not used here. */
    reason?: string;
  }[],
): HoursExceptionIndex {
  const index: HoursExceptionIndex = new Map();
  for (const exception of exceptions) {
    index.set(exception.date, {
      isClosed: exception.isClosed,
      open: exception.open,
      close: exception.close,
    });
  }
  return index;
}

/** The slots that apply on `date`: the exception's if one exists, else the weekday's. */
function resolveDaySlots(
  date: Date,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): { start: string; end: string }[] {
  const exception = exceptions?.get(format(date, 'yyyy-MM-dd'));
  if (exception) {
    if (exception.isClosed || exception.open === undefined || exception.close === undefined) {
      return [];
    }
    return [{ start: exception.open, end: exception.close }];
  }
  // JS: Sun=0..Sat=6 → ISO: Mon=1..Sun=7
  const isoWeekday = date.getDay() === 0 ? 7 : date.getDay();
  return hours[isoWeekday] ?? [];
}

/**
 * The workshop's opening spans for one date, in minutes from midnight, sorted
 * and merged. Empty when the workshop is closed that day.
 *
 * Pass `exceptions` to honour per-date overrides; without them this reads the
 * weekly pattern alone, which is what callers that only know the weekday want.
 */
export function getOpeningRangesForDate(
  date: Date,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): MinuteRange[] {
  const daySlots = resolveDaySlots(date, hours, exceptions);
  const ranges = daySlots
    .map((slot) => ({
      startMinutes: Math.round(parseTimeToHours(slot.start) * 60),
      endMinutes: Math.round(parseTimeToHours(slot.end) * 60),
    }))
    .filter((r) => r.endMinutes > r.startMinutes)
    .sort((a, b) => a.startMinutes - b.startMinutes);

  const merged: MinuteRange[] = [];
  for (const range of ranges) {
    const previous = merged[merged.length - 1];
    if (previous && range.startMinutes <= previous.endMinutes) {
      previous.endMinutes = Math.max(previous.endMinutes, range.endMinutes);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}

/** True when the workshop has no opening hours at all on `date`. */
export function isClosedOnDate(
  date: Date,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): boolean {
  return getOpeningRangesForDate(date, hours, exceptions).length === 0;
}

/**
 * `hours` with each weekday key in [`start`, `end`] replaced by what actually
 * applies on that date. For the calendar library's `availability.weekly`, which
 * is keyed by weekday and so cannot name a date on its own. (Its
 * `availability.specific` is no help: it is OR-ed with `weekly`, so it can add
 * availability but never remove it — exactly what a holiday needs.)
 *
 * Works whenever the window covers each weekday AT MOST ONCE, which is true of
 * both the day view and the week view. A longer window — the month view — maps
 * several dates onto one key, where a single date's override would leak onto
 * the others; there the plain weekly pattern is returned unchanged (#1302).
 */
export function applyRangeOverridesToWeekly(
  start: Date,
  end: Date,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): CalendarHours {
  if (exceptions === undefined || exceptions.size === 0) return hours;

  const dates: Date[] = [];
  for (
    let cursor = startOfDay(start);
    cursor <= end && dates.length <= 7;
    cursor = addDays(cursor, 1)
  ) {
    dates.push(cursor);
  }
  // More than a week: a weekday key would have to stand for several dates.
  if (dates.length > 7) return hours;

  const overridden = dates.filter((date) => exceptions.has(format(date, 'yyyy-MM-dd')));
  if (overridden.length === 0) return hours;

  const result = { ...hours };
  for (const date of overridden) {
    const isoWeekday = date.getDay() === 0 ? 7 : date.getDay();
    result[isoWeekday] = resolveDaySlots(date, hours, exceptions);
  }
  return result;
}

/**
 * Every opening span in [`start`, `end`] as absolute timestamps, one entry per
 * open stretch per date, with per-date exceptions applied.
 *
 * This is how a window LONGER than a week states its opening hours to the
 * calendar library, which `applyRangeOverridesToWeekly` cannot do: pair these
 * with {@link ALL_WEEKDAYS_CLOSED} as `availability.weekly` and the library's
 * `isAvailable` — `specific` match wins, otherwise fall through to `weekly`,
 * where an empty weekday means unavailable — marks exactly these spans open and
 * everything else closed. That expresses a single closed date in a month window,
 * which a weekday-keyed pattern cannot (#1302).
 *
 * Returns an empty array for absurd windows (over a year), where the caller
 * should stay on the weekly pattern rather than build thousands of spans.
 */
export function buildOpenRangeTimestamps(
  start: Date,
  end: Date,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): { start: number; end: number }[] {
  const spans: { start: number; end: number }[] = [];
  const lastDay = startOfDay(end);
  let cursor = startOfDay(start);
  for (let guard = 0; cursor <= lastDay; guard += 1) {
    if (guard > 400) return [];
    for (const range of getOpeningRangesForDate(cursor, hours, exceptions)) {
      spans.push({
        start: cursor.getTime() + range.startMinutes * 60_000,
        end: cursor.getTime() + range.endMinutes * 60_000,
      });
    }
    cursor = addDays(cursor, 1);
  }
  return spans;
}

/**
 * A weekly pattern where every day is closed, in the `Date.getDay()` convention
 * the library reads. Used as the baseline under `availability.specific`, whose
 * spans are the only thing that then counts as open.
 */
export const ALL_WEEKDAYS_CLOSED: CalendarHours = {
  0: [],
  1: [],
  2: [],
  3: [],
  4: [],
  5: [],
  6: [],
};

/** Single-date form of {@link applyRangeOverridesToWeekly}, for the day view. */
export function applyDateOverrideToWeekly(
  date: Date,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): CalendarHours {
  return applyRangeOverridesToWeekly(date, date, hours, exceptions);
}

/**
 * The next (`direction: 1`) or previous (`direction: -1`) date the workshop is
 * open, skipping days it is closed. Used by the day view's prev/next
 * navigation so browsing Friday → forward lands on Monday instead of walking
 * through a closed weekend (#1276).
 *
 * Always returns a date: when no weekday has opening hours — an unconfigured
 * tenant, or the moment before `businessHours` has loaded — every candidate
 * looks closed, so after a full week of probing this falls back to a plain
 * one-day step rather than looping forever.
 *
 * With `exceptions`, holidays are skipped too. The probe window grows to 21
 * days so a run of closed days longer than a week — a Christmas shutdown, a
 * renovation — is stepped over instead of landing on one.
 */
export function stepToOpenDay(
  date: Date,
  direction: 1 | -1,
  hours: CalendarHours,
  exceptions?: HoursExceptionIndex,
): Date {
  const probeDays = exceptions !== undefined && exceptions.size > 0 ? 21 : 7;
  for (let step = 1; step <= probeDays; step += 1) {
    const candidate = addDays(date, direction * step);
    if (!isClosedOnDate(candidate, hours, exceptions)) return candidate;
  }
  return addDays(date, direction);
}

/**
 * The parts of [axisStart, axisEnd] the workshop is *not* open — the gaps
 * before, between and after `openRanges`. Used to shade closed hours in the
 * vertical day view, which the calendar library does not shade itself (#1255).
 */
export function getClosedRangesWithin(
  axis: MinuteRange,
  openRanges: MinuteRange[],
): MinuteRange[] {
  const closed: MinuteRange[] = [];
  let cursor = axis.startMinutes;
  for (const open of openRanges) {
    const start = Math.max(open.startMinutes, axis.startMinutes);
    const end = Math.min(open.endMinutes, axis.endMinutes);
    if (end <= cursor) continue;
    if (start > cursor) closed.push({ startMinutes: cursor, endMinutes: start });
    cursor = end;
  }
  if (cursor < axis.endMinutes) {
    closed.push({ startMinutes: cursor, endMinutes: axis.endMinutes });
  }
  return closed;
}

/** Checks if a given Date falls within the workshop's opening hours for that weekday */
export function isTimeWithinBusinessHours(date: Date, hours: CalendarHours): boolean {
  // JS: Sun=0..Sat=6 → ISO: Mon=1..Sun=7
  const isoWeekday = date.getDay() === 0 ? 7 : date.getDay();
  const daySlots = hours[isoWeekday];
  if (!daySlots || daySlots.length === 0) return false;
  const timeHours = date.getHours() + date.getMinutes() / 60;
  return daySlots.some((slot) => {
    const start = parseTimeToHours(slot.start);
    const end = parseTimeToHours(slot.end);
    return timeHours >= start && timeHours < end;
  });
}
